import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Plus, Play, PhoneCall, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = {
  identified: "Identified", ready_for_outreach: "Ready for Outreach", contacted: "Contacted",
  interested: "Interested", not_interested: "Not Interested", appointment_scheduled: "Appointment Scheduled",
  applied: "Applied", issued: "Issued", declined: "Declined", suppressed: "Suppressed",
};

const STATUS_BADGE = {
  identified: "secondary", ready_for_outreach: "default", contacted: "default",
  interested: "default", not_interested: "outline", appointment_scheduled: "default",
  applied: "default", issued: "default", declined: "destructive", suppressed: "destructive",
};

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-xl font-bold">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function pct(rate) {
  return rate === null || rate === undefined ? "—" : `${Math.round(rate * 1000) / 10}%`;
}

export default function ClientOpportunities() {
  const [campaigns, setCampaigns] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [creatingHI, setCreatingHI] = useState(false);
  const [running, setRunning] = useState(false);
  const [queuingId, setQueuingId] = useState(null);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns");
      const opp = data.filter((c) => c.campaign_type === "client_opportunity");
      setCampaigns(opp);
      if (opp.length && !selectedCampaignId) setSelectedCampaignId(opp[0].id);
    } catch {
      setCampaigns([]);
    }
  };

  useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCampaignData = async (campaignId) => {
    if (!campaignId) { setMetrics(null); setOpportunities(null); return; }
    try {
      const { data } = await api.get(`/client-opportunities/campaigns/${campaignId}/metrics`);
      setMetrics(data);
    } catch (err) { setMetrics(null); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    try {
      const { data } = await api.get("/client-opportunities", { params: { campaign_id: campaignId } });
      setOpportunities(data);
    } catch { setOpportunities([]); }
  };

  useEffect(() => { loadCampaignData(selectedCampaignId); }, [selectedCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const createHospitalIndemnity = async () => {
    setCreatingHI(true);
    try {
      const { data } = await api.post("/client-opportunities/campaigns/hospital-indemnity");
      toast.success(`Hospital Indemnity campaign ready (${data.display_id || data.name})`);
      await loadCampaigns();
      setSelectedCampaignId(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCreatingHI(false); }
  };

  const runSelection = async () => {
    if (!selectedCampaignId) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/client-opportunities/campaigns/${selectedCampaignId}/select`);
      toast.success(
        `${data.created} identified, ${data.suppressed} suppressed, ${data.not_matched} not matched, `
        + `${data.skipped_existing} already had an opportunity`
      );
      await loadCampaignData(selectedCampaignId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const queueOutreach = async (oppId) => {
    setQueuingId(oppId);
    try {
      const { data } = await api.post(`/client-opportunities/${oppId}/ready-for-outreach`);
      if (data.status === "suppressed") toast.error("Client is now suppressed (DNC/opt-out) — not queued");
      else toast.success("Queued for Sophia outreach via the existing follow-up queue");
      await loadCampaignData(selectedCampaignId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setQueuingId(null); }
  };

  const filteredOpps = (opportunities || []).filter(
    (o) => statusFilter === "all" || o.status === statusFilter
  );

  return (
    <div className="space-y-4">
      <PageHeader title="Client Opportunities" subtitle="Additional-coverage opportunities identified from your existing client base.">
        <Button variant="outline" size="sm" onClick={createHospitalIndemnity} disabled={creatingHI} data-testid="create-hi-campaign">
          {creatingHI ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
          Hospital Indemnity Campaign
        </Button>
      </PageHeader>

      {campaigns === null ? (
        <div className="text-sm text-muted-foreground">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-xl border bg-card p-8 text-center">
          <Target className="mx-auto h-8 w-8 text-[#315A7D]" />
          <p className="mt-2 text-sm text-muted-foreground">
            No client-opportunity campaigns yet. Create the Hospital Indemnity campaign to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
              <SelectTrigger className="w-72"><SelectValue placeholder="Select a campaign" /></SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} — {c.offer}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={runSelection} disabled={running || !selectedCampaignId}>
              {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
              Run Selection
            </Button>
            <Button size="sm" variant="outline" onClick={() => loadCampaignData(selectedCampaignId)}>
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
          </div>

          {metrics && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                <Stat label="Active Clients" value={metrics.active_existing_clients} />
                <Stat label="Clients Screened" value={metrics.clients_screened} hint="as of last selection run" />
                <Stat label="Already Holding" value={metrics.clients_already_holding} />
                <Stat label="Identified" value={metrics.opportunities_identified_total} />
                <Stat label="Suppressed" value={metrics.suppressed} />
                <Stat label="Ready for Outreach" value={metrics.ready_for_outreach} />
                <Stat label="Contacted" value={metrics.contacted} hint={`contact rate ${pct(metrics.rates?.contact_rate)}`} />
                <Stat label="Interested" value={metrics.interested} hint={`interest rate ${pct(metrics.rates?.interest_rate)}`} />
                <Stat label="Appointments" value={metrics.appointments_scheduled} hint={`conversion ${pct(metrics.rates?.appointment_conversion)}`} />
                <Stat label="Applications" value={metrics.applications} hint={`conversion ${pct(metrics.rates?.application_conversion)}`} />
                <Stat label="Issued" value={metrics.issued} hint={`conversion ${pct(metrics.rates?.issue_conversion)}`} />
                <Stat label="Declined" value={metrics.declined} />
              </div>
              <div className="rounded-lg border bg-card p-3 text-xs text-muted-foreground">
                {metrics.penetration?.available
                  ? `Penetration: ${metrics.penetration.clients_holding} holding / `
                    + `${metrics.penetration.eligible_population} eligible (by campaign age/state criteria) `
                    + `= ${pct(metrics.penetration.penetration_rate)}`
                  : `Penetration: ${metrics.penetration?.reason || "not available"}`
                  + (metrics.penetration?.clients_holding !== undefined
                    ? ` (${metrics.penetration.clients_holding} of ${metrics.penetration.active_clients_total} active clients hold this product)`
                    : "")}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Offer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reasons</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Outreach</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Linked</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOpps.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground">
                    No opportunities match this filter.
                  </TableCell></TableRow>
                )}
                {filteredOpps.map((o) => (
                  <TableRow key={o.id} data-testid={`opportunity-row-${o.id}`}>
                    <TableCell>
                      <div className="font-medium">{o.client_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.client_id || "—"}</div>
                    </TableCell>
                    <TableCell>{o.offer || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[o.status] || "secondary"}>{STATUS_LABELS[o.status] || o.status}</Badge>
                      {o.suppressed && (
                        <div className="mt-1">
                          <Badge variant="destructive" title={o.suppression_reason || ""}>Suppressed</Badge>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate" title={(o.reasons || []).join("; ") || "—"}>
                      {(o.reasons || []).join("; ") || "—"}
                    </TableCell>
                    <TableCell>{o.assigned_to || "—"}</TableCell>
                    <TableCell className="text-xs">
                      <div>Last: {o.last_outreach_at ? new Date(o.last_outreach_at).toLocaleDateString() : "—"}</div>
                      <div className="text-muted-foreground">Next: {o.next_follow_up_date || "—"}</div>
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate" title={o.outcome || ""}>{o.outcome || "—"}</TableCell>
                    <TableCell className="text-xs">
                      <div>Appt: {o.appointment_id ? "Linked" : "—"}</div>
                      <div className="text-muted-foreground">Policy: {o.policy_id ? "Linked" : "—"}</div>
                    </TableCell>
                    <TableCell>{o.updated_at ? new Date(o.updated_at).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      {o.status === "identified" && !o.suppressed && (
                        <Button size="sm" variant="outline" disabled={queuingId === o.id} onClick={() => queueOutreach(o.id)}>
                          {queuingId === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
